import { useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import Icon from '../../../../components/icon'
import apiUrl from '../../../../configs/constant'
import {
    Box,
    Grid,
    Typography,
    Divider,
    Fab,
    Button,
    FormControl,
    TextField,
    FormHelperText,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material'

interface FormInputs {
    syscabang_id: any
    kode: any
    mmsatuan_id: any
    mmbarang_class_id:any
    nama_barang: any
}

export default function Add() {
    const userData = JSON.parse(localStorage.getItem('user')!)
    const token = userData.token
    const navigate = useNavigate()
    const cabang = JSON.parse(localStorage.getItem('cabang')!)
    const [searchParams] = useSearchParams();
    const pclass:any = searchParams.get('class') || '';

    const makloonMap: Record<number, string> = {
        12604000001: "JASA MAKLOON PRINTING",
        12604000002: "JASA MAKLOON BORDIR",
        12604000003: "JASA MAKLOON JAHIT",
        12604000004: "JASA MAKLOON PACKING",
        12604000005: "JASA MAKLOON LAINNYA",
    };

    const getMakloonName = (id: any): any => {
        return makloonMap[id] ?? "UNKNOWN";
    };

    const [satuanOptions, setSatuanOptions] = useState<any[]>([])

    // Fetch select options
    useEffect(() => {
        fetchCombo('/mm/satuan/list-combo', setSatuanOptions)
    }, [])

    const fetchCombo = (endpoint: string, setter: (data: any[]) => void) => {
        axios
            .get(apiUrl.apiUrl + endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(res => {
                if (res.data.success) setter(res.data.data)
            })
    }

    const defaultValues: Partial<FormInputs> = {
        syscabang_id: cabang,
        mmbarang_class_id:pclass,
        kode: '',
        mmsatuan_id: '',
        nama_barang: '',
    }

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm<FormInputs>({ defaultValues })

    const onSubmit = (data: FormInputs) => {
        Swal.fire({
            title: 'Konfirmasi',
            text: 'Apakah anda ingin menyimpan data ini ?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#337ab7',
            cancelButtonColor: '#ffc107',
            confirmButtonText: 'Simpan'
        }).then(result => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Now loading',
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading()
                    }
                })

                const url = apiUrl.apiUrl + '/purchasing/master-makloon/add'
                axios
                    .post(url, data, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                    .then(response => {
                        if (response.data.success) {
                            Swal.fire({
                                title: 'Pemberitahuan',
                                text: response.data.message,
                                icon: 'success',
                                timer: 1000,
                                timerProgressBar: true,
                                willClose: () => {
                                    window.history.back()
                                }
                            })
                        } else {
                            Swal.fire({
                                title: 'Pemberitahuan',
                                text: response.data.message,
                                icon: 'error'
                            })
                        }
                    })
                    .catch(error => {
                        Swal.fire({
                            title: 'Pemberitahuan',
                            text: error.message,
                            icon: 'error'
                        })
                    })
            }
        })
    }

    return (
        <Grid container spacing={6} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Grid item xl={12} md={12} xs={12}>
                <Fab variant='extended' color='primary' sx={{ '& svg': { mr: 1 } }} onClick={() => window.history.back()}>
                    <Icon icon='mdi:arrow-left' />
                    Kembali
                </Fab>
                <div className={'content-block dx-card'} style={{ padding: 20 }}>
                    <Typography variant='h5' sx={{ textAlign: 'center', marginBottom: 2 }}>
                        {`BUAT ${getMakloonName(pclass)}`}
                    </Typography>
                    <Divider sx={{ m: 3 }} />
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={12}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='nama_barang'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <TextField
                                                label='Nama Jasa'
                                                {...field}
                                                error={Boolean(errors.nama_barang)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    {errors.nama_barang && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <Controller
                                        name='kode'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <TextField
                                                label='Kode'
                                                {...field}
                                                error={Boolean(errors.kode)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        )}
                                    />
                                    {errors.kode && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id='satuan-label'>Satuan</InputLabel>
                                    <Controller
                                        name='mmsatuan_id'
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                labelId='satuan-label'
                                                label='Satuan'
                                                {...field}
                                                error={Boolean(errors.mmsatuan_id)}
                                            >
                                                {satuanOptions.map(opt => (
                                                    <MenuItem key={opt.id} value={opt.id}>{opt.nama}</MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.mmsatuan_id && (
                                        <FormHelperText sx={{ color: 'error.main' }}>Form ini tidak boleh kosong.</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Button variant='contained' type='submit'>
                                        SIMPAN
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </Grid>
        </Grid>
    )
}